import { type FC, useCallback, useEffect, useRef, useState } from "react";

import { Plus, Trash2 } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import { sentryUtils } from "@/shared/utils/sentry";

export interface EmployerEntry {
  name: string;
  startYear: string;
  endYear: string;
}

const ANSWER_KEY = "employment_info";

const currentYear = new Date().getFullYear();
const years = Array.from({ length: currentYear - 1920 + 1 }, (_, i) => String(currentYear - i));

const emptyEmployer: EmployerEntry = { name: "", startYear: "", endYear: "" };

function parseEmployers(raw: string | string[] | undefined): EmployerEntry[] {
  if (!Array.isArray(raw) || raw.length === 0) return [{ ...emptyEmployer }];
  try {
    return raw.map((s) => JSON.parse(s) as EmployerEntry);
  } catch (err) {
    sentryUtils.captureError(err, "EmploymentForm parse failed", undefined, "warning");
    // fall through
  }
  return [{ ...emptyEmployer }];
}

function serializeEmployers(employers: EmployerEntry[]): string[] {
  return employers.map((emp) => JSON.stringify(emp));
}

export function validateEmployers(
  answers: Record<string, string | string[]>,
): Record<string, string> {
  const employers = parseEmployers(answers[ANSWER_KEY]);
  const errors: Record<string, string> = {};

  for (let i = 0; i < employers.length; i++) {
    const emp = employers[i];
    const label = i === 0 ? "the most recent employer" : `previous employer ${i}`;

    if (!emp.name.trim()) {
      errors[ANSWER_KEY] = `Please enter the employer name for ${label}.`;
      return errors;
    }
    if (!emp.startYear || !emp.endYear) {
      errors[ANSWER_KEY] = `Please select start and end years for ${emp.name || label}.`;
      return errors;
    }
    if (Number(emp.startYear) > Number(emp.endYear)) {
      errors[ANSWER_KEY] = `Start year cannot be after end year for ${emp.name}.`;
      return errors;
    }
  }

  if (employers.length > 1) {
    const mostRecentEnd = Number(employers[0].endYear);
    for (let i = 1; i < employers.length; i++) {
      const prevEnd = Number(employers[i].endYear);
      if (prevEnd > mostRecentEnd) {
        errors[ANSWER_KEY] =
          `"${employers[i].name}" ends after your most recent employer "${employers[0].name}". Please reorder so the most recent employer is first.`;
        return errors;
      }
    }
  }

  return errors;
}

export const EmploymentForm: FC<FlowCustomComponentProps> = ({
  onComplete,
  answers,
  setAnswer,
  errors,
}) => {
  const [employers, setEmployers] = useState<EmployerEntry[]>(() =>
    parseEmployers(answers[ANSWER_KEY]),
  );

  const syncToAnswers = useCallback(
    (updated: EmployerEntry[]) => {
      setAnswer(ANSWER_KEY, serializeEmployers(updated));
    },
    [setAnswer],
  );

  useEffect(() => {
    if (!answers[ANSWER_KEY]) {
      syncToAnswers(employers);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const raw = answers[ANSWER_KEY];
    if (
      Array.isArray(raw) &&
      JSON.stringify(raw) !== JSON.stringify(serializeEmployers(employers))
    ) {
      setEmployers(parseEmployers(raw));
    }
  }, [answers[ANSWER_KEY]]); // eslint-disable-line react-hooks/exhaustive-deps

  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  const updateEmployer = (index: number, field: keyof EmployerEntry, value: string) => {
    setEmployers((prev) => {
      const updated = prev.map((emp, i) => (i === index ? { ...emp, [field]: value } : emp));
      syncToAnswers(updated);
      return updated;
    });
  };

  const updateNameDebounced = (index: number, value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => updateEmployer(index, "name", value), 400);
  };

  const addEmployer = () => {
    setEmployers((prev) => {
      const updated = [...prev, { ...emptyEmployer }];
      syncToAnswers(updated);
      return updated;
    });
  };

  const removeEmployer = (index: number) => {
    setEmployers((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      syncToAnswers(updated);
      return updated;
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col rounded-lg border border-primary bg-white p-6">
        {employers.map((employer, index) => (
          <div key={index}>
            {index > 0 && <hr className="my-6" />}

            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <Label required>
                  {index === 0 ? "Most recent employer" : `Previous employer ${index}`}
                </Label>
                {employers.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeEmployer(index)}
                    className="text-secondary hover:text-primary"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>

              <Input
                key={`name-${index}-${employers.length}`}
                placeholder="Employer name"
                defaultValue={employer.name}
                onChange={(e) => updateNameDebounced(index, e.target.value)}
                className={
                  errors?.[ANSWER_KEY] && !employer.name.trim()
                    ? "border-destructive border-[2px]"
                    : ""
                }
              />

              <div>
                <Label className="mb-2 inline-block" required>
                  Dates of employment
                </Label>
                <div className="flex items-center gap-3">
                  <Select
                    value={employer.startYear}
                    onValueChange={(val) => updateEmployer(index, "startYear", val)}
                  >
                    <SelectTrigger
                      className={`w-full${errors?.[ANSWER_KEY] && employer.name.trim() && (!employer.startYear || !employer.endYear) ? " border-destructive border-[2px]" : ""}`}
                    >
                      <SelectValue placeholder="Start year" />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((year) => (
                        <SelectItem key={year} value={year}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={employer.endYear}
                    onValueChange={(val) => updateEmployer(index, "endYear", val)}
                  >
                    <SelectTrigger
                      className={`w-full${errors?.[ANSWER_KEY] && employer.name.trim() && (!employer.startYear || !employer.endYear) ? " border-destructive border-[2px]" : ""}`}
                    >
                      <SelectValue placeholder="End year" />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((year) => (
                        <SelectItem key={year} value={year}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        ))}

        <FieldError message={errors?.[ANSWER_KEY]} className="mt-4" />

        <Button variant="outline" className="mt-6 w-full" onClick={addEmployer}>
          <Plus className="h-4 w-4" />
          Add a previous employer
        </Button>
      </div>

      <div className="flex flex-col items-center gap-2">
        <Button
          variant="brand"
          rounded
          className="w-full max-w-[400px]"
          onClick={() => onComplete()}
        >
          Continue
        </Button>

        <Button
          variant="link"
          className="text-secondary"
          onClick={() => onComplete({ skipValidation: true })}
        >
          Skip
        </Button>
      </div>
    </div>
  );
};
